/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useRef, useEffect, useLayoutEffect, useState, useMemo } from "react";
import { Map as MapLibreMap, Marker } from "maplibre-gl";
import { useSpring, a, config } from "@react-spring/web";
import { assembleDateTime, canShowGeolocationControls, createMapStyleLifecycle, ensureCoreMapImages, ensureTripMapRouteLayers, formatStalePositionAge, formatDelayMessage, getDepartedStopRouteColor, getTripMapInitialView, getTripMapNextStopDetails, getPreferredStopName, getRouteLineContrastColor, getTripStopRowId, pluralise, stopIdMatches, createCatchyMap, getMaplibreStyle, timeTidyPlus, useTripMapCamera, useTripMapLocationState } from "@catchy/app-core";
import BackLink from "./BackLink";
import { getDrawerBackdropProgress, updateDrawerBackgroundInteractivity, useMobileDrawer } from "./useMobileDrawer";
import Chevron from "@catchy/assets/svgs/chevron-up.svg";
import Clock from "@catchy/assets/svgs/clock.svg";
import Compass from "@catchy/assets/svgs/compass.svg";
import Layers from "@catchy/assets/svgs/layers.svg";
import MyLocation from "@catchy/assets/svgs/my-location.svg";
import ShareApple from "@catchy/assets/svgs/share-apple.svg";
import ShareNormal from "@catchy/assets/svgs/share-normal.svg";
import Spinner from "@catchy/assets/svgs/spinner.svg";
import VehicleIcon from "@catchy/assets/svgs/vehicle-icon.svg";
import VehiclePositionMarker from "@catchy/assets/svgs/vehicle-position-marker.svg";
import CrushedIcon from "@catchy/assets/svgs/occupancy/occupancy-crushed.svg";
import EmptyIcon from "@catchy/assets/svgs/occupancy/occupancy-empty.svg";
import FewSeatsIcon from "@catchy/assets/svgs/occupancy/occupancy-few-seats.svg";
import FullIcon from "@catchy/assets/svgs/occupancy/occupancy-full.svg";
import SeatsAvailableIcon from "@catchy/assets/svgs/occupancy/occupancy-seats-available.svg";
import StandingIcon from "@catchy/assets/svgs/occupancy/occupancy-standing.svg";
const getNewDirectionAngle = (direction, newDirection) => {
  const deltaClockwise = (newDirection - direction + 360) % 360;
  const deltaCounterClockwise = (direction - newDirection + 360) % 360;
  return deltaClockwise <= deltaCounterClockwise ? direction + deltaClockwise : direction - deltaCounterClockwise;
};
function getDistance(lat1, lon1, lat2, lon2, unit) {
  if (lat1 == lat2 && lon1 == lon2) {
    return 0;
  } else {
    var radlat1 = Math.PI * lat1 / 180;
    var radlat2 = Math.PI * lat2 / 180;
    var theta = lon1 - lon2;
    var radtheta = Math.PI * theta / 180;
    var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    if (dist > 1) {
      dist = 1;
    }
    dist = Math.acos(dist);
    dist = dist * 180 / Math.PI;
    dist = dist * 60 * 1.8532;
    return dist;
  }
}
function getOccupancyDetails(status) {
  switch (status) {
    case "EMPTY":
      return {
        description: "Empty",
        colorClass: "text-green-600 dark:text-green-400 opacity-50",
        IconComponent: EmptyIcon
      };
    case "MANY_SEATS_AVAILABLE":
      return {
        description: "Seats available",
        colorClass: "text-green-600 dark:text-green-400",
        IconComponent: SeatsAvailableIcon
      };
    case "FEW_SEATS_AVAILABLE":
      return {
        description: "Few seats available",
        colorClass: "text-amber-400 dark:text-amber-300",
        IconComponent: FewSeatsIcon
      };
    case "STANDING_ROOM_ONLY":
      return {
        description: "Standing room only",
        colorClass: "text-orange-400",
        IconComponent: StandingIcon
      };
    case "CRUSHED_STANDING_ROOM_ONLY":
      return {
        description: "Very limited standing room",
        colorClass: " text-rose-600/80 dark:text-rose-500/80",
        IconComponent: CrushedIcon
      };
    case "FULL":
      return {
        description: <>
            <span className="font-bold">Full.</span> May not be accepting
            passengers
          </>,
        colorClass: " text-rose-600/80 dark:text-rose-500/80",
        IconComponent: FullIcon
      };
    case "NOT_ACCEPTING_PASSENGERS":
      return {
        description: "Not accepting passengers.",
        colorClass: " text-rose-600/80 dark:text-rose-500/80",
        IconComponent: FullIcon
      };
    case "NOT_BOARDABLE":
      return {
        description: "Not boardable.",
        colorClass: " text-rose-600/80 dark:text-rose-500/80",
        IconComponent: FullIcon
      };
    case "NO_DATA_AVAILABLE":
      return {
        description: "No data available.",
        colorClass: "text-zinc-400/60",
        IconComponent: FullIcon
      };
    default:
      return {
        description: "Invalid status.",
        colorClass: "text-zinc-400/60",
        IconComponent: FullIcon
      };
  }
}
function getStopIcon(busOrTrain, isDark, preferSatellite) {
  const icons = {
    bus: "bus-stop-col",
    train: "train-station-col",
    ferry: "ferry-wharfs-col"
  };
  const baseIcon = icons[busOrTrain] || "bus-stop-col";
  const suffix = isDark || preferSatellite ? "dark" : "light";
  return `${baseIcon}-${suffix}`;
}
function buildTripMapStopLabelEmphasis(stopId, destinationStopId) {
  return ["any", ["==", ["get", "stop_id"], stopId ?? null], ["==", ["get", "stop_id"], destinationStopId ?? null]];
}
const StopCircle = ({
  className,
  style,
  feature
}) => <svg viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg" className={`h-[1.135rem] w-[1.135rem] ${className}`} style={style}>
    <circle cx="9" cy="9" r="9" fill="currentColor" />
    <circle cx="9" cy="9" r={feature ? 5 : 4} fill="white" />
  </svg>;
const MapStopRow = ({
  currentStatus,
  currentStop,
  delay,
  destination,
  first,
  id,
  isDark,
  rowRef,
  last,
  name,
  next,
  index,
  route_color,
  scheduledTime,
  stopId,
  startDate,
  time
}) => {
  const departureTimeDate = useMemo(() => assembleDateTime(startDate, scheduledTime), [startDate, scheduledTime]);
  const tidyTime = useMemo(() => timeTidyPlus(departureTimeDate, time, delay), [departureTimeDate, time, delay]);
  const isCurrentStop = index === next;
  const isUserStop = id === stopId;
  const isKeyStop = isUserStop || destination;
  const isFinishedStop = last && (index < next || isCurrentStop && Boolean(currentStatus) && (currentStatus === "STOPPED_AT" || tidyTime.seconds <= 0));
  const isWaitingToStart = first && isCurrentStop && tidyTime.seconds > 0 && Boolean(currentStatus);
  const currentStopLabel = (() => {
    if (isFinishedStop) {
      if (destination) return "Finished at your destination:";
      return isUserStop ? "Finished at your stop:" : "Finished at:";
    }
    if (isWaitingToStart) {
      if (destination) return "Waiting at your destination:";
      return isUserStop ? "Waiting at your stop:" : "Waiting to start:";
    }
    if (first && isCurrentStop) {
      return "Trip starts here:";
    }
    if (!isCurrentStop) {
      return null;
    }
    switch (currentStatus) {
      case "INCOMING_AT":
        if (destination) return "Arriving at your destination:";
        return isUserStop ? "Arriving at your stop:" : "Arriving at:";
      case "STOPPED_AT":
        if (destination) return "Stopped at your destination:";
        return isUserStop ? "Stopped at your stop:" : "Stopped at:";
      case "IN_TRANSIT_TO":
      default:
        if (destination) return "Your destination is next:";
        return isUserStop ? "Your stop is next:" : "Next:";
    }
  })();
  return <div className="flex flex-nowrap justify-between rounded-md pr-5" ref={node => {
    if (index === next || last && next > index) {
      currentStop.current = node;
    }
    if (rowRef) {
      rowRef(node);
    }
  }} id={id} style={{
    "--trip-stop-route-color": route_color,
    "--trip-stop-departed-route-color": getDepartedStopRouteColor(route_color, isDark)
  }}>
      <div className="flex">
        <div className={`trip-map-stop-marker relative flex w-12 flex-shrink-0 items-center justify-center pr-3 ${index < next ? "trip-map-stop-marker--departed" : ""}`}>
          {!first && <StopConnector className="absolute top-0 h-1/2 w-1" />}
          <StopCircle className={`relative z-10`} feature={last || first} />
          {!last && <StopConnector className="absolute top-1/2 h-1/2 w-1" />}
        </div>
        <div className="inline-flex items-center py-3 leading-none">
          <div className="flex flex-col justify-center">
            {currentStopLabel ? <div className={`block w-full text-sm leading-none ${first && isCurrentStop ? "opacity-60" : "font-semibold"}`}>
                {currentStopLabel}
              </div> : id === stopId && <div className="block w-full text-sm font-bold leading-none">
                  Your stop:
                </div>}
            {!currentStopLabel && destination && <div className="block w-full text-sm font-bold leading-none">
                Your destination
              </div>}
            <span className={`block text-pretty leading-none ${isKeyStop ? "text-lg font-semibold" : "font-normal"}`}>
              {name}
            </span>
          </div>
        </div>
      </div>
      <div className="ml-6 flex shrink-0 flex-col items-end justify-center whitespace-nowrap py-3 text-right tabular-nums">
        {isFinishedStop || index < next ? <span className="leading-none opacity-60">
            {last ? "Finished" : "Departed"}
          </span> : <>
            <div className={isKeyStop ? "text-lg leading-none" : "leading-none"}>
              {tidyTime.seconds < 70 && index === next ? <span className="font-semibold">Due</span> : Math.round(tidyTime.seconds / 60) <= 0 ? "Under 1 min" : pluralise(Math.round(tidyTime.seconds / 60), "min")}
            </div>

            <span className="block text-sm leading-none opacity-60">
              {tidyTime.time}
            </span>
          </>}
      </div>
    </div>;
};
const StopConnector = ({
  className,
  style
}) => <svg viewBox="0 0 10 10" xmlns="http://www.w3.org/2000/svg" className={className} style={style} fill="currentColor">
    <rect x="-100" y="-100" width="200" height="400" />
  </svg>;
const shareLink = (title, text, url) => {
  if (navigator.share) {
    navigator.share({
      url: url
    }).catch(error => {
      if (error?.name === "AbortError" || /cancell?ation of share/i.test(error?.message || "")) {
        return;
      }
      console.error("Share error: ", error);
    });
  }
};
const desktopBreakpoint = 1024;
const landscapeBreakpoint = 720;
const sideBySideAspectRatio = 1.4;
const defaultPanelWidth = 420;
const defaultHeaderHeight = 250;
const mobileClosedExtraHeight = 88;
const mobileMinimumOpenExtraHeight = 96;
const mobileOpenExtraHeight = 88;
const mobileOpenScreenFraction = 0.36;
const mobileDrawerFullTopGap = 10;
const getDefaultStopPublicId = stop => stop?.stop_id;
const defaultStopIdMatches = (stop, publicStopId) => stopIdMatches(stop?.stop_id, publicStopId);
const Map = ({
  RouteBadge,
  isApple,
  bearingOffset,
  busOrTrain,
  canShare,
  currentStatus,
  delaySeconds = 0,
  delayMinutes,
  destinationStopId,
  getStopPublicId = getDefaultStopPublicId,
  isDark,
  isValidating,
  matchesStopId = defaultStopIdMatches,
  occupancy,
  preferredStopNames,
  preferSatellite,
  route_color,
  route_text_color,
  routeLabel,
  route_type,
  regionalDefaultCenter,
  schoolBusSchool,
  shareUrl,
  setPreferSatellite,
  shape_color,
  shape,
  stopId,
  startDate,
  stops,
  stopTimeUpdate,
  time,
  tripTitle,
  useLocalStorage,
  vehiclePosition
}) => {
  const mapContainerRef = useRef();
  const mobileChromeRef = useRef();
  const mobileMapRef = useRef();
  const mobileInlineRecentreRef = useRef();
  const threeDShadowHolderRef = useRef();
  const dropShadowHolderRef = useRef();
  const map = useRef();
  const marker = useRef();
  const pendingVehicleAnimationRef = useRef(null);
  const panelRef = useRef();
  const sheetTop = useRef();
  const currentStop = useRef();
  const stopsScrollRef = useRef();
  const stopRowRefs = useRef(new globalThis.Map());
  const userIsManipulatingListRef = useRef(false);
  const autoScrollResumeTimeoutRef = useRef(null);
  const userLocationMarkerRef = useRef(null);
  const initialViewRef = useRef(null);
  const lastValidVehiclePositionRef = useRef(null);
  const visibilityStateRef = useRef({
    isDesktop: false,
    panelExpanded: false,
    panelWidth: defaultPanelWidth,
    mobileExpandedHeight: 0,
    mobileClosedHeight: 0,
    viewportWidth: 0,
    viewportHeight: 0
  });
  const length = stops?.length;
  const referredStop = stops?.find(stop => matchesStopId(stop, stopId));
  const destinationStop = stops?.find(stop => destinationStopId ? matchesStopId(stop, destinationStopId) : false);
  const destinationIndex = stops?.findIndex(stop => destinationStopId ? matchesStopId(stop, destinationStopId) : false);
  const referredStopIndex = stops?.findIndex(stop => stopId ? matchesStopId(stop, stopId) : false);
  const distanceToRefferedStop = referredStop && vehiclePosition && getDistance(vehiclePosition.latitude, vehiclePosition.longitude, referredStop?.stop_lat, referredStop?.stop_lon, "K");
  const [viewport, setViewport] = useState({
    width: 0,
    height: 0
  });
  const [panelWidth, setPanelWidth] = useState(defaultPanelWidth);
  const [headerHeight, setHeaderHeight] = useState(defaultHeaderHeight);
  const [firstScroll, setFirstScroll] = useState(true);
  const [rotated, setRotated] = useState(false);
  const [rotation, setRotation] = useState(false);
  const [pitch, setPitch] = useState(false);
  const [markerinView, setMarkerInView] = useState(true);
  const [mapReadyRevision, setMapReadyRevision] = useState(0);
  const {
    preferenceHydrated,
    trackingEnabled,
    geolocationPermission,
    geolocating,
    userLocation,
    isUserTrackingActive,
    mapPitch,
    routeFallback,
    toggleUserLocation
  } = useTripMapLocationState({
    useLocalStorage,
    preferSatellite,
    shape,
    stops,
    regionalDefault: regionalDefaultCenter
  });
  const bearing = vehiclePosition?.bearing ?? 0;
  const vehiclePositionStale = Boolean(vehiclePosition?.positionStale);
  const stalePositionAge = formatStalePositionAge(vehiclePosition?.lastPositionReportedAt, time);
  // The initial live-trip response can omit a position before a later refresh
  // supplies one. Treat both null and undefined as absent so the marker mount
  // lifecycle reruns when that first usable position arrives.
  const hasVehiclePosition = Boolean(vehiclePosition);
  const [listScrolled, setListScrolled] = useState(false);
  const viewportHeight = viewport.height || (typeof window === "undefined" ? 0 : window.innerHeight);
  const isWideLandscape = viewport.width >= landscapeBreakpoint && viewport.width / Math.max(viewportHeight, 1) >= sideBySideAspectRatio;
  const isDesktop = viewport.width >= desktopBreakpoint || isWideLandscape;
  const mobileClosedHeight = headerHeight + mobileClosedExtraHeight;
  const mobileExpandedHeight = Math.max(mobileClosedHeight + mobileMinimumOpenExtraHeight, Math.round(viewportHeight * mobileOpenScreenFraction) + mobileOpenExtraHeight);
  const mobileFullHeight = Math.max(mobileExpandedHeight, Math.round(viewportHeight - mobileDrawerFullTopGap));
  const drawerDetents = ["closed", "open", "full"];
  const {
    y,
    isOpen,
    isFullOpen,
    drawerHeight,
    open,
    close,
    headerBind,
    bodyBind,
    openHeightRef: openYRef,
    fullHeightRef: fullYRef
  } = useMobileDrawer({
    isDesktop,
    openHeight: mobileExpandedHeight,
    closedHeight: mobileClosedHeight,
    fullHeight: mobileFullHeight,
    scrollRef: stopsScrollRef,
    detents: drawerDetents,
    desktopSnap: "open"
  });
  const panelExpanded = isDesktop ? true : isOpen;
  const getLayoutState = () => ({
    isDesktop,
    panelExpanded,
    panelWidth,
    mobileExpandedHeight,
    mobileClosedHeight,
    viewportWidth: viewport.width,
    viewportHeight
  });
  visibilityStateRef.current = getLayoutState();
  const routeLineContrastColor = getRouteLineContrastColor(shape_color, isDark);
  const currentTimestamp = Date.parse(time);
  const nextStopDetails = useMemo(() => {
    return getTripMapNextStopDetails(startDate, stops, stopTimeUpdate, currentStatus, delaySeconds, currentTimestamp);
  }, [currentStatus, currentTimestamp, delaySeconds, startDate, stopTimeUpdate, stops]);
  let nextStopInSequence = nextStopDetails?.stopOrder;
  const currentStopForHeader = Number.isFinite(nextStopInSequence) && nextStopInSequence >= 0 ? stops?.[nextStopInSequence] : null;
  const currentStopScheduledDate = useMemo(() => {
    if (!currentStopForHeader?.departure_time) return null;
    return assembleDateTime(startDate, currentStopForHeader.departure_time);
  }, [currentStopForHeader?.departure_time, startDate]);
  const currentStopTiming = useMemo(() => {
    if (!currentStopScheduledDate) return null;
    return timeTidyPlus(currentStopScheduledDate, time, Number(delaySeconds) || 0);
  }, [currentStopScheduledDate, delaySeconds, time]);
  const isFinishedAtLastStop = Boolean(currentStopForHeader && nextStopInSequence === (stops?.length ?? 0) - 1 && currentStatus && (currentStatus === "STOPPED_AT" || currentStopTiming?.seconds <= 0));
  const panelStatusLabel = isFinishedAtLastStop ? matchesStopId(currentStopForHeader, stopId) ? "Finished at your stop:" : "Finished at:" : null;
  const destinationStopDelta = typeof destinationIndex === "number" && destinationIndex >= 0 && Number.isFinite(nextStopInSequence) ? destinationIndex - nextStopInSequence : null;
  const referredStopDelta = typeof referredStopIndex === "number" && referredStopIndex >= 0 && Number.isFinite(nextStopInSequence) ? referredStopIndex - nextStopInSequence : null;
  const hasPassedReferredStop = referredStopDelta !== null ? referredStopDelta < 0 : true;
  const shouldShowDestinationDelta = destinationStop && Number.isFinite(destinationStopDelta) && hasPassedReferredStop;
  const occupancyDetails = getOccupancyDetails(occupancy);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateViewport = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };
    updateViewport();
    window.addEventListener("resize", updateViewport);
    return () => {
      window.removeEventListener("resize", updateViewport);
    };
  }, []);
  useLayoutEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const observers = [];
    if (panelRef.current && isDesktop) {
      const updatePanelWidth = () => {
        if (!panelRef.current) return;
        setPanelWidth(panelRef.current.clientWidth || defaultPanelWidth);
      };
      updatePanelWidth();
      const observer = new ResizeObserver(() => {
        updatePanelWidth();
      });
      observer.observe(panelRef.current);
      observers.push(observer);
    }
    if (sheetTop.current) {
      const updateHeaderHeight = () => {
        if (!sheetTop.current) return;
        setHeaderHeight(sheetTop.current.clientHeight || defaultHeaderHeight);
      };
      updateHeaderHeight();
      const observer = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      observer.observe(sheetTop.current);
      observers.push(observer);
    }
    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, [isDesktop]);
  const hideMobileMapOverlays = !isDesktop && drawerHeight > mobileExpandedHeight + 32;
  const getMapPadding = (open = panelExpanded, layout = getLayoutState()) => {
    if (layout.isDesktop) {
      return {
        top: 100,
        bottom: 80,
        left: (open ? layout.panelWidth : 0) + 32,
        right: 80
      };
    }
    const maxBottomPadding = Math.max(0, layout.viewportHeight - 140);
    const bottomPadding = Math.min(open ? layout.mobileExpandedHeight + 24 : layout.mobileClosedHeight + 24, maxBottomPadding);
    return {
      top: 100,
      bottom: bottomPadding,
      left: 80,
      right: 80
    };
  };
  const getOcclusionInsets = () => {
    const layout = visibilityStateRef.current;
    if (layout.isDesktop) {
      return {
        left: layout.panelWidth + 32,
        bottom: 0
      };
    }
    return {
      bottom: layout.panelExpanded ? layout.mobileExpandedHeight : layout.mobileClosedHeight,
      left: 0
    };
  };

  // Determine if a given LngLat is hidden behind UI, not merely inside padded camera space.
  const isMarkerOnscreen = lngLat => {
    if (!map.current || !lngLat) return true;
    const container = map.current.getContainer();
    const w = container?.clientWidth ?? 0;
    const h = container?.clientHeight ?? 0;
    if (w === 0 || h === 0) return true;
    const pt = map.current.project(lngLat);
    const layout = visibilityStateRef.current;
    const insets = getOcclusionInsets();
    const margin = 8; // px tolerance near the edges
    const result = layout.isDesktop ? pt.x >= insets.left - margin && pt.x <= w + margin && pt.y >= -margin && pt.y <= h + margin : pt.x >= -margin && pt.x <= w + margin && pt.y >= -margin && pt.y <= h - insets.bottom + margin;
    return result;
  };
  useEffect(() => {
    const removeUserLocationMarker = () => {
      if (userLocationMarkerRef.current) {
        userLocationMarkerRef.current.remove();
        userLocationMarkerRef.current = null;
      }
    };
    if (!map.current || !trackingEnabled || !userLocation) {
      removeUserLocationMarker();
      return undefined;
    }
    if (!userLocationMarkerRef.current) {
      const customMarkerEl = document.createElement("div");
      customMarkerEl.className = "catchy-user-location-dot";
      userLocationMarkerRef.current = new Marker({
        element: customMarkerEl,
        anchor: "center"
      }).setLngLat(userLocation).addTo(map.current);
    } else {
      userLocationMarkerRef.current.setLngLat(userLocation);
    }
    return () => {
      if (!trackingEnabled) removeUserLocationMarker();
    };
  }, [mapReadyRevision, trackingEnabled, userLocation]);
  useEffect(() => {
    if (!preferenceHydrated) return undefined;
    const useDarkThemeAccents = isDark || preferSatellite;
    function getMapStyle() {
      return getMaplibreStyle({
        isDark,
        preferSatellite
      });
    }
    function mountVehicleMarker() {
      if (!map.current) return;
      if (marker.current) {
        marker.current.remove();
        const clonedSvg = document.querySelector("#cloned-vehiclePositionMarker-box");
        clonedSvg && clonedSvg.parentNode?.removeChild(clonedSvg);
        marker.current = null;
      }
      if (!vehiclePosition) return;
      const markerTemplate = document.querySelector("#vehiclePositionMarker");
      if (!markerTemplate) return;
      const pendingVehicleAnimation = pendingVehicleAnimationRef.current;
      const markerLng = pendingVehicleAnimation?.fromLng ?? vehiclePosition.longitude;
      const markerLat = pendingVehicleAnimation?.fromLat ?? vehiclePosition.latitude;
      const markerBearing = pendingVehicleAnimation?.fromBearing ?? bearing;
      const cloneMarkerSvg = markerTemplate.cloneNode(true);
      cloneMarkerSvg.setAttribute("id", "cloned-vehiclePositionMarker-box");
      cloneMarkerSvg.style.opacity = vehiclePositionStale ? "0.5" : "1";
      marker.current = new Marker({
        rotation: markerBearing,
        pitchAlignment: "map",
        rotationAlignment: "map",
        element: cloneMarkerSvg
      });
      marker.current.setLngLat([markerLng, markerLat]).setRotation(markerBearing + (bearingOffset ? bearingOffset : 0)).addTo(map.current);
      const canvasContainer = map.current.getCanvasContainer();
      if (canvasContainer && dropShadowHolderRef.current && threeDShadowHolderRef.current) {
        canvasContainer.appendChild(dropShadowHolderRef.current);
        dropShadowHolderRef.current.appendChild(threeDShadowHolderRef.current);
        threeDShadowHolderRef.current.appendChild(marker.current.getElement());
      }
      if (pendingVehicleAnimation) {
        pendingVehicleAnimationRef.current = null;
        toLng.set(markerLng);
        toLat.set(markerLat);
        toBearing.set(markerBearing);
        toLng.start(pendingVehicleAnimation.targetLng);
        toLat.start(pendingVehicleAnimation.targetLat);
        toBearing.start(pendingVehicleAnimation.targetBearing);
      }
    }
    async function addSourcesAndLayers(revision, isCurrent) {
      await ensureCoreMapImages(map.current, {
        isDark,
        preferSatellite
      });
      if (!map.current || !isCurrent?.(revision)) return;
      const {
        routeShapeBeforeLayerId
      } = ensureTripMapRouteLayers(map.current, {
        shape,
        outerRouteColor: useDarkThemeAccents ? routeLineContrastColor : shape_color,
        innerRouteColor: useDarkThemeAccents ? shape_color : routeLineContrastColor
      });
      const tripStopsGeojson = {
        type: "FeatureCollection",
        features: stops?.map(stop => {
          const preferredName = getPreferredStopName(preferredStopNames, stop.stop_id, getStopPublicId(stop));
          return {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [stop.stop_lon, stop.stop_lat]
            },
            properties: {
              title: preferredName ? preferredName : stop.stop_name,
              stop_id: getStopPublicId(stop),
              icon_image: destinationStopId && matchesStopId(stop, destinationStopId) ? `stop-destination-${isDark || preferSatellite ? "dark" : "light"}` : getStopIcon(busOrTrain, isDark, preferSatellite),
              is_destination_stop: Boolean(destinationStopId && matchesStopId(stop, destinationStopId))
            }
          };
        })
      };
      if (!map.current.getSource("tripStops")) {
        map.current.addSource("tripStops", {
          type: "geojson",
          data: tripStopsGeojson
        });
      } else {
        map.current.getSource("tripStops").setData(tripStopsGeojson);
      }
      if (!map.current.getLayer("tripStops")) {
        const emphasizedStopExpression = buildTripMapStopLabelEmphasis(stopId, destinationStopId);
        map.current.addLayer({
          id: "tripStops",
          type: "symbol",
          source: "tripStops",
          layout: {
            "icon-image": ["to-string", ["get", "icon_image"]],
            "icon-allow-overlap": true,
            "icon-anchor": "bottom",
            "icon-ignore-placement": true,
            "icon-offset": ["case", ["boolean", ["get", "is_destination_stop"], false], ["literal", [10, 0]], ["literal", [0, 0]]],
            "icon-padding": 10,
            "icon-size": ["interpolate", ["linear"], ["zoom"], 10, ["case", emphasizedStopExpression, 0.3, 0.2], 12, ["case", emphasizedStopExpression, 0.8, 0.72], 13, ["case", emphasizedStopExpression, 0.94, 0.9], 14, 1, 22, 1],
            "text-allow-overlap": true,
            "text-field": ["step", ["zoom"], "", 15, "", 16, ["to-string", ["get", "title"]]],
            "text-font": ["case", emphasizedStopExpression, ["literal", ["Bus SemiBold"]], ["literal", ["Bus Regular"]]],
            "text-justify": "auto",
            "text-letter-spacing": 0.03,
            "text-line-height": 1.1,
            "text-max-width": 20,
            "text-offset": ["case", ["boolean", ["get", "is_destination_stop"], false], ["literal", [0.625, 0]], ["literal", [0, 0]]],
            "text-padding": 0,
            "text-radial-offset": 1.7,
            "text-size": ["case", emphasizedStopExpression, 16, 14],
            "text-variable-anchor": ["bottom-right", "bottom-left"]
          },
          paint: {
            "icon-translate": [0, 0],
            "text-opacity": 1,
            "text-halo-color": useDarkThemeAccents ? "#282d39" : "#FFFFFF",
            "text-halo-width": useDarkThemeAccents ? 1.52 : 2,
            "text-color": useDarkThemeAccents ? "#FFFFFF" : "#282d39",
            "text-translate": [0, 6]
          }
        });
      }
      if (map.current.getLayer("tripStops")) {
        const emphasizedStopExpression = buildTripMapStopLabelEmphasis(stopId, destinationStopId);
        map.current.setLayoutProperty("tripStops", "icon-image", ["to-string", ["get", "icon_image"]]);
        map.current.setLayoutProperty("tripStops", "icon-size", ["interpolate", ["linear"], ["zoom"], 10, ["case", emphasizedStopExpression, 0.3, 0.2], 12, ["case", emphasizedStopExpression, 0.8, 0.72], 13, ["case", emphasizedStopExpression, 0.94, 0.9], 14, 1, 22, 1]);
        map.current.setLayoutProperty("tripStops", "icon-offset", ["case", ["boolean", ["get", "is_destination_stop"], false], ["literal", [10, 0]], ["literal", [0, 0]]]);
        map.current.setLayoutProperty("tripStops", "text-offset", ["case", ["boolean", ["get", "is_destination_stop"], false], ["literal", [0.625, 0]], ["literal", [0, 0]]]);
        map.current.setLayoutProperty("tripStops", "text-font", ["case", emphasizedStopExpression, ["literal", ["Bus SemiBold"]], ["literal", ["Bus Regular"]]]);
        map.current.setLayoutProperty("tripStops", "text-size", ["case", emphasizedStopExpression, 16, 14]);
        map.current.setPaintProperty("tripStops", "icon-translate", [0, 0]);
        map.current.moveLayer("tripStops");
      }
      const routeShapeTopBoundaryLayerId = routeShapeBeforeLayerId || (map.current.getLayer("tripStops") ? "tripStops" : undefined);
      if (map.current.getLayer("routelayer")) {
        map.current.moveLayer("routelayer", routeShapeTopBoundaryLayerId);
      }
      if (map.current.getLayer("routelayerinner")) {
        map.current.moveLayer("routelayerinner", routeShapeTopBoundaryLayerId);
      }
      mountVehicleMarker();
      setPitch(map.current.getPitch());
      if (isCurrent?.(revision)) setMapReadyRevision(revision);
    }
    ;
    const handleRotateStart = () => {
      setRotated(true);
    };
    const handleRotate = () => {
      setRotation(map.current.getBearing());
    };
    const handlePitch = () => {
      setPitch(map.current.getPitch());
    };
    const handleMoveEnd = () => {
      if (marker.current?._lngLat !== undefined) {
        const visible = isMarkerOnscreen(marker.current._lngLat);
        setMarkerInView(visible);
      }
    };
    if (!map.current) {
      if (!initialViewRef.current) {
        initialViewRef.current = getTripMapInitialView({
          vehiclePosition,
          trackingEnabled,
          userLocation,
          routeFallback
        });
      }
      map.current = createCatchyMap(MapLibreMap, {
        container: mapContainerRef.current,
        style: null,
        center: initialViewRef.current.center,
        pitch: mapPitch,
        zoom: initialViewRef.current.zoom,
        minZoom: 9,
        maxZoom: 18,
        maxPitch: 40
      });
      map.current.setPadding({
        ...getMapPadding()
      });
      setPitch(map.current.getPitch());
    }
    const styleLifecycle = createMapStyleLifecycle({
      map: map.current,
      onReady: (revision, isCurrent) => addSourcesAndLayers(revision, isCurrent)
    });
    styleLifecycle.beginStyle(getMapStyle());
    map.current.on("rotatestart", handleRotateStart);
    map.current.on("rotate", handleRotate);
    map.current.on("pitch", handlePitch);
    map.current.on("moveend", handleMoveEnd);
    return () => {
      if (!map.current) return;
      styleLifecycle.dispose();
      map.current.off("rotatestart", handleRotateStart);
      map.current.off("rotate", handleRotate);
      map.current.off("pitch", handlePitch);
      map.current.off("moveend", handleMoveEnd);
    };
  }, [hasVehiclePosition, isDark, preferenceHydrated, preferSatellite]);
  useEffect(() => {
    marker.current?.getElement()?.style.setProperty("opacity", vehiclePositionStale ? "0.5" : "1");
  }, [vehiclePositionStale]);
  useEffect(() => {
    return () => {
      marker.current?.remove();
      marker.current = null;
      userLocationMarkerRef.current?.remove();
      userLocationMarkerRef.current = null;
      map.current?.remove();
      map.current = null;
      if (typeof window !== "undefined") window.__CATCHY_MAP__ = null;
    };
  }, []);
  const springCoordinate = vehiclePosition ? [vehiclePosition.longitude, vehiclePosition.latitude] : getTripMapInitialView({
    vehiclePosition: null,
    trackingEnabled,
    userLocation,
    routeFallback
  }).center;
  const {
    toLng,
    toLat,
    toBearing
  } = useSpring({
    immediate: false,
    toLng: springCoordinate[0],
    toLat: springCoordinate[1],
    toBearing: bearing,
    from: {
      toLng: springCoordinate[0],
      toLat: springCoordinate[1],
      toBearing: bearing
    },
    onChange: props => {
      marker.current?.setLngLat([props.value.toLng, props.value.toLat]).setRotation(props.value.toBearing + (bearingOffset ? bearingOffset : 0));
    },
    onRest: () => marker.current?._lngLat !== undefined && (() => {
      const visible = isMarkerOnscreen(marker.current._lngLat);
      setMarkerInView(visible);
    })(),
    config: config.slow
  });
  useEffect(() => {
    if (!vehiclePosition) {
      marker.current?.remove();
      marker.current = null;
      pendingVehicleAnimationRef.current = null;
      lastValidVehiclePositionRef.current = null;
      return;
    }
    const previousPosition = lastValidVehiclePositionRef.current;
    const nextBearing = getNewDirectionAngle(toBearing.get(), bearing);
    if (marker.current && previousPosition) {
      toLng.start(vehiclePosition.longitude);
      toLat.start(vehiclePosition.latitude);
      toBearing.start(nextBearing);
    } else {
      pendingVehicleAnimationRef.current = null;
      toLng.set(vehiclePosition.longitude);
      toLat.set(vehiclePosition.latitude);
      toBearing.set(nextBearing);
    }
    lastValidVehiclePositionRef.current = vehiclePosition;
  }, [bearing, toBearing, toLat, toLng, vehiclePosition]);
  const {
    recentre: handleRecentre,
    showRecentre
  } = useTripMapCamera({
    mapRef: map,
    readyRevision: mapReadyRevision,
    enabled: preferenceHydrated,
    trackingEnabled,
    preferSatellite,
    vehiclePosition,
    userLocation,
    routeFallback,
    padding: getMapPadding(panelExpanded)
  });
  const scrollListTo = useCallback((top, immediate = false) => {
    const scroller = stopsScrollRef.current;
    if (!scroller || !Number.isFinite(top)) return;
    scroller.scrollTo({
      top,
      behavior: immediate ? "auto" : "smooth"
    });
  }, []);
  const pauseListAutoScroll = useCallback((resumeDelay = 1800) => {
    userIsManipulatingListRef.current = true;
    if (autoScrollResumeTimeoutRef.current) {
      window.clearTimeout(autoScrollResumeTimeoutRef.current);
    }
    autoScrollResumeTimeoutRef.current = window.setTimeout(() => {
      userIsManipulatingListRef.current = false;
      autoScrollResumeTimeoutRef.current = null;
    }, resumeDelay);
  }, []);
  const scrollToStopIndex = useCallback(index => {
    if (!Number.isFinite(index) || index < 0) return;
    const row = stopRowRefs.current.get(index);
    if (!row) return;
    pauseListAutoScroll(2400);
    scrollListTo(row.offsetTop, false);
  }, [pauseListAutoScroll, scrollListTo]);
  useEffect(() => {
    if (userIsManipulatingListRef.current) {
      return;
    }
    const timeoutId = window.setTimeout(() => {
      scrollListTo(currentStop.current?.offsetTop || 0, firstScroll);
      setFirstScroll(false);
    }, 200);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [firstScroll, nextStopInSequence, scrollListTo]);
  useEffect(() => {
    const scroller = stopsScrollRef.current;
    if (!scroller) return;
    const updateScrollState = () => {
      setListScrolled(scroller.scrollTop > 4);
    };
    const handleTouchStart = () => {
      pauseListAutoScroll();
    };
    const handlePointerDown = () => {
      pauseListAutoScroll();
    };
    const handleWheel = () => {
      pauseListAutoScroll();
    };
    updateScrollState();
    scroller.addEventListener("scroll", updateScrollState, {
      passive: true
    });
    scroller.addEventListener("touchstart", handleTouchStart, {
      passive: true
    });
    scroller.addEventListener("pointerdown", handlePointerDown, {
      passive: true
    });
    scroller.addEventListener("wheel", handleWheel, {
      passive: true
    });
    return () => {
      scroller.removeEventListener("scroll", updateScrollState);
      scroller.removeEventListener("touchstart", handleTouchStart);
      scroller.removeEventListener("pointerdown", handlePointerDown);
      scroller.removeEventListener("wheel", handleWheel);
    };
  }, [isDesktop, pauseListAutoScroll]);
  useEffect(() => {
    return () => {
      if (autoScrollResumeTimeoutRef.current) {
        window.clearTimeout(autoScrollResumeTimeoutRef.current);
      }
    };
  }, []);
  useEffect(() => {
    const targets = [mobileChromeRef.current, mobileMapRef.current, mobileInlineRecentreRef.current].filter(Boolean);
    updateDrawerBackgroundInteractivity({
      activeElement: typeof document === "undefined" ? null : document.activeElement,
      drawer: panelRef.current,
      hidden: !isDesktop && isFullOpen,
      targets
    });
  }, [isDesktop, isFullOpen]);
  const panelContent = <>
      <div {...!isDesktop ? headerBind() : {}} ref={sheetTop} className={`pointer-events-auto z-20 px-[20px] py-3 transition-shadow ${listScrolled ? "shadow-lg" : ""} ${isDesktop ? "" : "touch-none"}`}>
        {!isDesktop && <svg viewBox="0 0 30 4" xmlns="http://www.w3.org/2000/svg" width="30" height="4" className="mx-auto mb-1.5 opacity-30">
            <rect x="0" width="30" height="4" rx="2" fill="currentColor" />
          </svg>}
        <div>
          <div className={`${isDesktop ? "pt-1.5" : ""} mb-3 flex items-start`}>
            <RouteBadge route_color={route_color} route_text_color={route_text_color} route_type={route_type} routeLabel={routeLabel} className="mr-3 h-9 w-9 shrink-0" />
            <div className="w-full">
              <h1 className="text-pretty pt-1.5 text-2xl font-semibold leading-none">
                {stops[nextStopInSequence]?.stop_headsign ? stops[nextStopInSequence]?.stop_headsign : tripTitle}
              </h1>
              {tripTitle === "School Bus" && <h2 className="mt-1.5 text-base font-normal leading-none opacity-60">
                  {schoolBusSchool}
                </h2>}
              {panelStatusLabel && <div className="mt-1.5 text-sm font-semibold leading-none opacity-60">
                  {panelStatusLabel}
                </div>}
            </div>
            {!isDesktop && <button aria-expanded={isOpen} aria-label={isOpen ? "Close trip details" : "Open trip details"} className="pointer-events-auto ml-3 grid h-9 w-9 flex-shrink-0 place-items-center rounded-full outline-none transition-all duration-150 hover:bg-zinc-400/10 focus-visible:ring active:bg-zinc-400/10" onClick={() => isOpen ? close() : open()}>
                <Chevron className={`h-6 w-6 transition-all ${isOpen ? "rotate-180" : "rotate-0"}`} />
              </button>}
          </div>

          {occupancy && <div className="mb-3 flex items-center text-pretty">
              <div className={`mr-3 flex w-9 flex-shrink-0 justify-center ${occupancyDetails.className}}`}>
                <occupancyDetails.IconComponent className={`h-4 w-4 ${occupancyDetails.colorClass}`} />
              </div>
              <div className="leading-tight">
                {occupancyDetails.description}
              </div>
            </div>}
          <div className="flex justify-between gap-x-4 text-pretty">
            <div className={`${delayMinutes > 2 || delayMinutes < -1 ? "font-bold text-rose-600 dark:text-rose-500" : ""} shrink mb-1 inline-flex items-center leading-none`}>
              <div className="mr-3 flex w-9 flex-shrink-0 justify-center">
                <Clock className="h-4 w-4 opacity-80" />
              </div>
              {formatDelayMessage(delayMinutes, referredStop && panelWidth < 420 ? "min" : "minute")}
            </div>

            {shouldShowDestinationDelta ? <button type="button" className="shrink mb-1 text-right leading-none opacity-60 outline-none ring-blue-500/50 focus-visible:ring" onClick={() => scrollToStopIndex(destinationIndex)}>
                {destinationStopDelta === 0 ? "At your destination" : destinationStopDelta > 0 ? `${destinationStopDelta} ${destinationStopDelta === 1 ? "stop" : "stops"} until your destination` : `${Math.abs(destinationStopDelta)} ${Math.abs(destinationStopDelta) === 1 ? "stop" : "stops"} past your destination`}
              </button> : referredStop && vehiclePosition && <button type="button" className="shrink mb-1 text-right leading-none opacity-60 outline-none ring-blue-500/50 focus-visible:ring" onClick={() => scrollToStopIndex(referredStopIndex)}>
                  {distanceToRefferedStop < 1 ? parseInt(distanceToRefferedStop * 1000) + "m" : distanceToRefferedStop.toFixed(1) + "km"}
                  {referredStopDelta !== null && referredStopDelta >= 0 ? " until your stop" : " past your stop"}
                </button>}
          </div>
        </div>
      </div>
      <div className="relative min-h-0 flex-1 pl-5">
        <a.div ref={stopsScrollRef} {...!isDesktop ? bodyBind() : {}} className={`pointer-events-auto box-border h-full min-h-0 overscroll-none pb-12 [scrollbar-gutter:stable] ${panelExpanded ? "overflow-y-auto" : "overflow-y-hidden"}`} style={{
        touchAction: isDesktop ? "auto" : "pan-y"
      }}>
          {stops?.map((el, k) => {
          const preferredName = getPreferredStopName(preferredStopNames, el.stop_id, getStopPublicId(el));
          const isDestination = Boolean(destinationStopId && matchesStopId(el, destinationStopId));
          return <MapStopRow name={preferredName ? preferredName : el.stop_name} delay={Number(delaySeconds) || 0} id={getTripStopRowId(getStopPublicId(el), stopId)} isDark={isDark} stopId={stopId} key={k} index={k} next={Number.isFinite(nextStopInSequence) ? nextStopInSequence : -1} currentStatus={currentStatus} route_color={route_color} route_type={route_type} routeLabel={routeLabel} first={k === 0} last={k === length - 1} destination={isDestination} time={time} currentStop={currentStop} rowRef={node => {
            if (node) {
              stopRowRefs.current.set(k, node);
            } else {
              stopRowRefs.current.delete(k);
            }
          }} startDate={startDate} scheduledTime={el.departure_time} />;
        })}
        </a.div>
      </div>
    </>;
  return <>
      <div ref={mobileChromeRef} className="pointer-events-none touch-none">
        <div className="row sticky top-0 z-20 mb-2 flex justify-between px-[20px] pb-2 pt-4">
          {!isDesktop && <BackLink onMap={true} />}

          {!isDesktop && <div className={`flex flex-nowrap gap-x-3 transition-opacity duration-200 ${hideMobileMapOverlays ? "pointer-events-none opacity-0" : "opacity-100"}`}>
              {isValidating && <div className="mr-1 grid h-9 w-9 place-items-center">
                  <Spinner className="h-[1.375rem] w-[1.375rem] animate-spin text-white drop-shadow" />
                </div>}

              <div className="flex flex-col gap-y-2">
                {canShare && <button onClick={() => shareLink("Route " + routeLabel + " | Catchy", tripTitle, shareUrl)} className="pointer-events-auto grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-white shadow-md outline-none transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 dark:bg-black dark:hover:bg-zinc-800 dark:active:bg-zinc-800">
                    {isApple ? <ShareApple className="h-6 w-6" title="Share" /> : <ShareNormal className="h-6 w-6" title="Share" />}
                  </button>}

                <button className="pointer-events-auto grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-white shadow-md outline-none transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 dark:bg-black dark:hover:bg-zinc-800 dark:active:bg-zinc-800" onClick={() => setPreferSatellite(!preferSatellite)}>
                  <Layers className="h-[1.25rem] w-[1.25rem]" title="Map Layer" />
                </button>

                {canShowGeolocationControls(geolocationPermission) && <button className="pointer-events-auto grid h-9 w-9 flex-shrink-0 place-items-center rounded-full bg-white shadow-md outline-none transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 dark:bg-black dark:hover:bg-zinc-800 dark:active:bg-zinc-800" onClick={toggleUserLocation} disabled={geolocating}>
                      {geolocating ? <Spinner className="h-4 w-4 animate-spin text-blue-500 dark:text-blue-300" /> : <MyLocation className={`h-4 w-4 ${isUserTrackingActive && "text-blue-500 dark:text-blue-300"}`} title="My Location" />}
                    </button>}

                <button className={`z-20 grid h-9 w-9 place-items-center rounded-full bg-white text-zinc-400 shadow-md transition-opacity dark:bg-black ${rotated ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`} onClick={() => {
              const markerLngLat = marker.current?._lngLat;
              map.current?.easeTo({
                bearing: 0,
                around: markerLngLat && map.current.getBounds().contains(markerLngLat) ? markerLngLat : map.current.getCenter()
              });
              setRotated(false);
            }} disabled={!rotated}>
                  <Compass className="relative h-7 w-7" style={{
                transform: `rotate(${-1 * rotation}deg)`
              }} />
                </button>
              </div>
            </div>}
        </div>
      </div>
      {(!vehiclePosition || vehiclePositionStale) && <div className="pointer-events-none absolute inset-x-0 z-10 flex pt-8" style={{
      paddingLeft: isDesktop ? panelWidth + 40 : 20,
      paddingRight: isDesktop ? 80 : 20
    }}>
          <div className="relative mx-auto flex max-w-xl flex-col gap-4 rounded-md bg-amber-100/85 px-4 py-3 text-zinc-900 backdrop-blur">
            <p>
              This {busOrTrain} isn’t reporting its location. GPS updates can
              be disrupted by tunnels or tall buildings.
            </p>
            {vehiclePositionStale ? <p className="opacity-65">Last updated {stalePositionAge}.</p> : ""}
          </div>
        </div>}
      <div ref={mobileMapRef} className="absolute inset-0 h-screen w-screen">
        <div ref={mapContainerRef} className="maplibre-map relative h-screen w-screen" />
      </div>

      <div className="z-0 hidden">
        <VehiclePositionMarker id="vehiclePositionMarker" width="50" height="50" className={!(isDark || preferSatellite) ? "text-blue-600" : "text-blue-500"} />
        <div style={{
        filter: `drop-shadow(0 ${pitch / 6.5}px 0 white)`
      }} ref={threeDShadowHolderRef} />
        <div style={{
        filter: `drop-shadow(0 ${pitch / 10}px 8px rgba(0 0 0 / .3))`
      }} ref={dropShadowHolderRef} />
      </div>

      {isDesktop ? <>
          <div className="pointer-events-none fixed inset-x-0 top-0 z-20 flex justify-between px-5 pb-2 pt-4">
            <div className="flex items-start">
              <BackLink onMap={true} />
              {isValidating && <div className="ml-3 grid h-9 w-9 place-items-center">
                  <Spinner className="h-[1.375rem] w-[1.375rem] animate-spin text-white drop-shadow" />
                </div>}
            </div>
            <div className="flex flex-col gap-y-2">
              {canShare && <button onClick={() => shareLink("Route " + routeLabel + " | Catchy", tripTitle, shareUrl)} className="pointer-events-auto grid h-9 w-9 place-items-center rounded-full bg-white shadow-md outline-none transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 dark:bg-black dark:hover:bg-zinc-800 dark:active:bg-zinc-800">
                  {isApple ? <ShareApple className="h-6 w-6" title="Share" /> : <ShareNormal className="h-6 w-6" title="Share" />}
                </button>}
              <button className="pointer-events-auto grid h-9 w-9 place-items-center rounded-full bg-white shadow-md outline-none transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 dark:bg-black dark:hover:bg-zinc-800 dark:active:bg-zinc-800" onClick={() => setPreferSatellite(!preferSatellite)}>
                <Layers className="h-[1.25rem] w-[1.25rem]" title="Map Layer" />
              </button>
              {canShowGeolocationControls(geolocationPermission) && <button className="pointer-events-auto grid h-9 w-9 place-items-center rounded-full bg-white shadow-md outline-none transition-all duration-150 hover:bg-zinc-100 focus-visible:ring active:bg-zinc-100 dark:bg-black dark:hover:bg-zinc-800 dark:active:bg-zinc-800" onClick={toggleUserLocation} disabled={geolocating}>
                    {geolocating ? <Spinner className="h-4 w-4 animate-spin text-blue-500 dark:text-blue-300" /> : <MyLocation className={`h-4 w-4 ${isUserTrackingActive && "text-blue-500 dark:text-blue-300"}`} title="My Location" />}
                  </button>}
              <button className={`z-20 grid h-9 w-9 place-items-center rounded-full bg-white text-zinc-400 shadow-md transition-opacity dark:bg-black ${rotated ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`} onClick={() => {
            const markerLngLat = marker.current?._lngLat;
            map.current?.easeTo({
              bearing: 0,
              around: markerLngLat && map.current.getBounds().contains(markerLngLat) ? markerLngLat : map.current.getCenter()
            });
            setRotated(false);
          }} disabled={!rotated}>
                <Compass className="relative h-7 w-7" style={{
              transform: `rotate(${-1 * rotation}deg)`
            }} />
              </button>
            </div>
          </div>
          <div className="pointer-events-none fixed inset-y-0 left-0 z-10">
            <div className="flex h-full flex-col justify-end px-4 pb-4">
              <div aria-label="Trip details" ref={panelRef} role="region" tabIndex={-1} className="full pointer-events-auto flex h-[calc(100vh-5rem)] w-[min(calc(40vw),26rem)] flex-col overflow-hidden rounded-2xl bg-white/80 bg-blend-color-dodge shadow-lg backdrop-blur dark:bg-black/80">
                {panelContent}
              </div>
            </div>
          </div>
          <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 flex justify-end px-5 pb-4">
            <button className={`flex h-9 flex-nowrap items-center rounded-full pl-1.5 pr-4 font-bold shadow-md outline-none transition-all duration-200 ${markerinView ? "bg-white text-blue-500 dark:bg-black dark:text-blue-300" : "bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-600 dark:hover:bg-blue-600 dark:active:bg-blue-600"} ${showRecentre ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`} onClick={handleRecentre} disabled={!showRecentre}>
              <VehicleIcon className="mr-2 h-6 w-6" />
              <span>Recentre</span>
            </button>
          </div>
        </> : <>
          <a.div aria-hidden="true" className={`fixed inset-0 z-20 bg-zinc-950/30 dark:bg-zinc-950/70 ${isFullOpen ? "pointer-events-auto" : "pointer-events-none"}`} style={{
        opacity: y.to(value => getDrawerBackdropProgress(value, openYRef.current, fullYRef.current))
      }} />
          <a.div ref={mobileInlineRecentreRef} className="pointer-events-none fixed inset-x-0 mx-auto w-full max-w-xl px-4" style={{
        bottom: y.to(value => `${value + 16}px`)
      }}>
            <div className="flex justify-end">
              <button className={`z-20 flex h-9 flex-nowrap items-center rounded-full pl-1.5 pr-4 font-bold shadow-md outline-none transition-all duration-200 ${markerinView ? "bg-white text-blue-500 dark:bg-black dark:text-blue-300" : "bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-600 dark:hover:bg-blue-600 dark:active:bg-blue-600"} ${showRecentre && !hideMobileMapOverlays ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"}`} onClick={handleRecentre} disabled={!showRecentre}>
                <VehicleIcon className="mr-2 h-6 w-6" />
                <span>Recentre</span>
              </button>
            </div>
          </a.div>
          <a.div className="pointer-events-auto fixed inset-x-0 bottom-0 z-30 mx-auto w-full max-w-xl touch-none select-none" style={{
        height: y
      }}>
            <div aria-label="Trip details" ref={panelRef} role="region" tabIndex={-1} className="flex h-full flex-col overflow-hidden rounded-t-2xl bg-white/80 bg-blend-color-dodge backdrop-blur dark:bg-black/80">
              {panelContent}
            </div>
          </a.div>
        </>}
    </>;
};
export default Map;